import React, { FC } from 'react';
import { Badge, Box, Card, Heading, HStack, Icon, SimpleGrid, Stack, Text } from '@chakra-ui/react';
import { LuCheck, LuX } from 'react-icons/lu';
import { Button } from '@/components/ui/button';
import { PLANS, Plan, PlanTier } from '@/data/plans';

interface PricingCardsProps {
  onSelect: (tier: Exclude<PlanTier, 'Enterprise'>) => void;
  onContact?: () => void;
  currentTier?: string;
}

const ctaLabel = (plan: Plan, currentTier?: string): string => {
  if (currentTier === plan.tier) return 'Current plan';
  if (plan.cta === 'contact') return 'Talk to sales';
  return `Start with ${plan.name}`;
};

const PricingCards: FC<PricingCardsProps> = ({ onSelect, onContact, currentTier }) => {
  const handleCta = (plan: Plan) => {
    if (plan.cta === 'contact') {
      onContact?.();
      return;
    }
    onSelect(plan.tier as Exclude<PlanTier, 'Enterprise'>);
  };

  return (
    <SimpleGrid columns={{ base: 1, md: 3 }} gap='6' alignItems='stretch'>
      {PLANS.map((plan) => (
        <Card.Root
          key={plan.tier}
          variant={plan.highlighted ? 'elevated' : 'outline'}
          borderWidth={plan.highlighted ? '2px' : '1px'}
          borderColor={plan.highlighted ? 'brand.400' : undefined}
          position='relative'
          h='full'
        >
          <Card.Body>
            <Stack gap='4' h='full'>
              <HStack justifyContent='space-between' alignItems='center' gap='2' minH='6'>
                <Text fontSize='sm' fontWeight='bold' textTransform='uppercase' letterSpacing='wide' color='brand.500'>
                  {plan.name}
                </Text>
                {plan.highlighted && (
                  <Badge colorPalette='brand' variant='solid'>
                    Most popular
                  </Badge>
                )}
              </HStack>

              <Box>
                <HStack alignItems='baseline' gap='1.5'>
                  <Heading size='3xl' lineHeight='1'>
                    {plan.priceLabel}
                  </Heading>
                  {plan.priceSuffix && (
                    <Text fontSize='sm' color='fg.muted'>
                      {plan.priceSuffix}
                    </Text>
                  )}
                </HStack>
                <Text fontSize='xs' color='fg.muted' mt='1'>
                  {plan.billingNote}
                </Text>
              </Box>

              <Text fontSize='sm' color='fg.muted' minH='10'>
                {plan.tagline}
              </Text>

              <Button
                data-testid={`cta-${plan.tier}`}
                colorScheme={plan.highlighted ? 'brand' : undefined}
                variant={plan.highlighted ? 'solid' : 'outline'}
                size='lg'
                onClick={() => handleCta(plan)}
                disabled={!!currentTier && currentTier === plan.tier}
              >
                {ctaLabel(plan, currentTier)}
              </Button>

              <Stack gap='1' py='3' borderTopWidth='1px' borderBottomWidth='1px' borderColor='border.muted'>
                <Text fontWeight='bold'>{plan.docsLabel}</Text>
                <Text fontSize='sm' color='fg.muted'>
                  {plan.storageLabel}
                </Text>
              </Stack>

              <Stack gap='2'>
                {plan.featuresIntro && (
                  <Text fontSize='xs' fontWeight='bold' textTransform='uppercase' letterSpacing='wide' color='fg.muted'>
                    {plan.featuresIntro}
                  </Text>
                )}
                {plan.features.map((f) => (
                  <HStack key={f.label} gap='2' alignItems='flex-start'>
                    <Icon color={f.included ? 'brand.500' : 'gray.300'} mt='0.5'>
                      {f.included ? <LuCheck /> : <LuX />}
                    </Icon>
                    <Text fontSize='sm' color={f.included ? 'fg' : 'fg.muted'}>
                      {f.label}
                    </Text>
                  </HStack>
                ))}
              </Stack>
            </Stack>
          </Card.Body>
        </Card.Root>
      ))}
    </SimpleGrid>
  );
};

export default PricingCards;
